<script lang="ts">
  import Banner from "@shared/svelte/Components/Banner/Banner.svelte";
  import CollateralSection from "@shared/svelte/Components/CollateralSection/CollateralSection.svelte";
  import ContentBlock from "@shared/svelte/Components/ContentBlock/ContentBlock.svelte";
  import CustomContentCarousel from "@shared/svelte/Components/Carousel/CustomContentCarousel.svelte";
  import RecipeCards from "@shared/svelte/Components/RecipeCards/RecipeCards.svelte";
  import SelfFetchingInternalBanner from "@shared/svelte/Components/Banner/SelfFetchingInternalBanner.svelte";
  import SelfFetchingBrowseBy from "@shared/svelte/Components/BrowseBy/SelfFetchingBrowseBy.svelte";
  import SelfFetchingCardCarousel from "@shared/svelte/Components/Carousel/SelfFetchingCardCarousel.svelte";
  import SelfFetchingCarrotBanner from "@shared/svelte/Components/Banner/SelfFetchingCarrotBanner.svelte";
  import SelfFetchingKevelBanner from "@shared/svelte/Components/Banner/SelfFetchingKevelBanner.svelte";
  import SelfFetchingMiniStories from "@shared/svelte/Components/MiniStories/SelfFetchingMiniStories.svelte";
  import SelfFetchingProductList from "@shared/svelte/Components/ProductList/SelfFetchingProductList.svelte";
  import ShoppableUnit from "@shared/svelte/Components/ShoppableUnit/ShoppableUnit.svelte";
  import Spotlight from "@shared/svelte/Components/Spotlight.svelte";
  import TitleComponent from "@shared/svelte/Components/TitleComponent.svelte";
  import ProductCatalogComponent from "@shared/svelte/Components/ProductCatalogComponent.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { SponsoredProductPageEntity } from "@shared/services/event_tracking/entities";

  export let configuration: any;
  export let mobileMode: boolean | null = null;
  export let pageName: string | null = null;
  export let pageValue: string | null = null;
  export let stateParams: any = {};
  export let carouselCardsShown: number = 4;
  export let Cart = null;

  // Whitelist of acceptable component types
  const showComponent = (componentConfig: any) => {
    return [
      "browse-by",
      "card-carousel",
      "carrot-banner",
      "collateral-section",
      "content-block",
      "custom-content-banner",
      "custom-content-product-carousel",
      "kevel-banner",
      "mini-stories",
      "product-catalog",
      "recipe-cards",
      "self-fetching-banner",
      "self-fetching-product-list",
      "shoppable-unit",
      "spotlight",
      "title-component",
    ].includes(componentConfig.type);
  };

  const getComponent = (componentConfig: any) => {
    switch (componentConfig.type) {
      case "browse-by": {
        return SelfFetchingBrowseBy;
      }
      case "card-carousel": {
        return SelfFetchingCardCarousel;
      }
      case "carrot-banner": {
        return SelfFetchingCarrotBanner;
      }
      case "kevel-banner": {
        return SelfFetchingKevelBanner;
      }
      case "collateral-section": {
        return CollateralSection;
      }
      case "content-block": {
        return ContentBlock;
      }
      case "custom-content-banner": {
        return Banner;
      }
      case "custom-content-product-carousel": {
        return CustomContentCarousel;
      }
      case "mini-stories": {
        return SelfFetchingMiniStories;
      }
      case "product-catalog": {
        return ProductCatalogComponent;
      }
      case "recipe-cards": {
        return RecipeCards;
      }
      case "self-fetching-banner": {
        return SelfFetchingInternalBanner;
      }
      case "self-fetching-product-list": {
        return SelfFetchingProductList;
      }
      case "shoppable-unit": {
        return ShoppableUnit;
      }
      case "spotlight": {
        return Spotlight;
      }
      case "title-component": {
        return TitleComponent;
      }
    }
  };

  const getProps = (componentConfig: any) => {
    switch (componentConfig.type) {
      case "browse-by": {
        return {
          headerText: componentConfig.header,
          pageName,
          pageValue,
        };
      }
      case "card-carousel": {
        return {
          id: componentConfig.id,
          carouselCardsShown: carouselCardsShown,
          headerText: componentConfig.header,
          outsideContainer: pageName == "home",
          shortenHeaderTextOnMobile: pageName != "home",
          sponsoredFlag: componentConfig.sponsored_flag,
          forwardLink: componentConfig.forward_url,
          recommendationMethod: componentConfig.recommendation_method,
          recommendationAttrs: componentConfig.recommendation_attrs,
          withoutMatchedDistributor: componentConfig.without_matched_distributor,
          endpoint: componentConfig.endpoint,
          carouselDataProps: componentConfig.carousel_data,
          productLinePriceBucket: componentConfig.product_line_price_bucket,
          pageName,
          pageValue,
        };
      }
      case "carrot-banner": {
        return {
          pageName,
          pageValue,
        };
      }
      case "kevel-banner": {
        return {
          pageName,
          pageValue,
        };
      }
      case "collateral-section": {
        return {
          mobileMode,
          trackingData: {
            id: componentConfig.id,
            position: componentConfig.position,
          },
          collaterals: componentConfig.collaterals_attributes,
        };
      }
      case "custom-content-banner": {
        return {
          banners: [
            {
              image_url: componentConfig.desktop_image_upload_url,
              image_alt_text: componentConfig.image_alt_text,
              slot_id: componentConfig.desktop_slot_id,
              custom_content_banner_id: componentConfig.id,
              position: componentConfig.position,
              display_sponsored_label: componentConfig.display_sponsored_label,
            },
            {
              image_url: componentConfig.mobile_image_upload_url,
              image_alt_text: componentConfig.image_alt_text,
              slot_id: componentConfig.mobile_slot_id,
              custom_content_banner_id: componentConfig.id,
              position: componentConfig.position,
              display_sponsored_label: componentConfig.display_sponsored_label,
            },
          ],
          pageName,
        };
      }
      case "custom-content-product-carousel": {
        return {
          carouselCardsShown,
          id: componentConfig.id,
          position: componentConfig.position,
          title: componentConfig.title_text,
          productLineIds: componentConfig.product_line_ids,
          productLinesApiUrl: componentConfig.productLinesApiUrl,
        };
      }
      case "recipe-cards": {
        return {
          recipeCards: componentConfig.recipe_cards_attributes,
          displaySponsoredLabel: componentConfig.display_sponsored_label,
          trackingData: {
            id: componentConfig.id,
            position: componentConfig.position,
          },
        };
      }
      case "self-fetching-banner": {
        return {
          pageName,
          pageValue,
        };
      }
      case "self-fetching-product-list": {
        return {
          stateParams,
          Cart,
          title: componentConfig.title_text,
          productLineIds: componentConfig.product_line_ids,
          productLinesApiUrl: componentConfig.productLinesApiUrl,
          layout: componentConfig.layout,
          trackingData: {
            id: componentConfig.id,
            position: componentConfig.position,
            layout: componentConfig.layout,
          },
        };
      }
      case "product-catalog": {
        return {
          Cart,
          mobileMode,
          title: componentConfig.title,
          baseStateParamType: componentConfig.base_state_param_type,
          baseStateParamId: componentConfig.base_state_param_id,
          id: componentConfig.id,
          position: componentConfig.position,
        };
      }
      case "shoppable-unit": {
        return {
          shouldFetchData: true,
          pageName,
        };
      }
      case "spotlight": {
        return {
          title: componentConfig.title,
          description: componentConfig.description,
          forward_text: componentConfig.forward_text,
          forward_url: componentConfig.forward_url,
          video_upload_url: componentConfig.video_upload_url,
          image_alt_text: componentConfig.image_alt_text,
          image_upload_url: componentConfig.image_upload_url,
          use_thumbnail: componentConfig.use_thumbnail,
          paid_tracking_values: componentConfig.paid_tracking_values,
          id: componentConfig.id,
          position: componentConfig.position,
        };
      }
      case "title-component": {
        return {
          description: componentConfig.description,
          header: componentConfig.header || componentConfig.title_text,
          displaySponsoredLabel: componentConfig.display_sponsored_label,
          trackingData: {
            id: componentConfig.id,
            position: componentConfig.position,
          },
        };
      }
      default: {
        return componentConfig;
      }
    }
  };

  const addSponsoredProductPageTracking = componentConfig => {
    if (componentConfig.sponsored_product_page_tracking) {
      return [new SponsoredProductPageEntity(componentConfig.sponsored_product_page_tracking)];
    } else {
      return [];
    }
  };
</script>

<style>
  .discovery-panel {
    display: flex;
    flex-direction: column;
  }
</style>

<div
  class="fizz-styles discovery-panel fizz-gap-16"
  data-testid="discovery-panel"
  class:fizz-stack-32={pageName === "home"}
  class:fizz-gap-32={pageName === "home"}
>
  {#each configuration || [] as componentConfig}
    {#if showComponent(componentConfig)}
      <TrackingContext entities={addSponsoredProductPageTracking(componentConfig)}>
        <svelte:component
          this={getComponent(componentConfig)}
          bind:mobileMode
          {...getProps(componentConfig)}
        />
      </TrackingContext>
    {/if}
  {/each}
</div>
